import { chain, contracts } from "../config";
import { getTableRows } from "./atomicApi";
import type { NftDrop } from "../types";

// Reads for the nftdrops and escrowbacker contracts. Neither has a REST
// index like AtomicHub's — these are plain chain RPC get_table_rows calls
// against the contracts directly. Table scopes/shapes here must match
// nftdrops.hpp / escrowbacker.hpp exactly; double check against those if
// something comes back empty unexpectedly.

export interface EscrowRate {
  template_id: number;
  collection_name: string;
  schema_name: string;
  backing: { quantity: string; contract: string };
  enabled: boolean;
}

/** The fixed amount (if any) escrowbacker requires to mint a backed NFT
 * from this template — the Mint page uses this so users never have to
 * guess an amount (which would just revert if it didn't match exactly). */
export async function getEscrowRate(templateId: number | string): Promise<EscrowRate | null> {
  const rows = await getTableRows<EscrowRate>({
    rpcUrl: chain.url,
    code: contracts.escrow,
    scope: contracts.escrow,
    table: "rates",
    lower_bound: String(templateId),
    upper_bound: String(templateId),
    limit: 1,
  });
  return rows[0] ?? null;
}

/** All configured drops (both plain and backed) — filter client-side for
 * what's currently active (enabled + within its time window). */
export async function listDrops(): Promise<NftDrop[]> {
  return getTableRows<NftDrop>({
    rpcUrl: chain.url,
    code: contracts.nftdrops,
    scope: contracts.nftdrops,
    table: "drops",
    limit: 100,
  });
}

/** One row of escrowbacker's `rates` table — every template that's been
 * registered as backable, whether or not anything has minted against it
 * yet. Used by the admin panel; getEscrowRate above is the single-row
 * lookup version used by the Mint page. */
export async function listRates(limit = 200): Promise<EscrowRate[]> {
  return getTableRows<EscrowRate>({
    rpcUrl: chain.url,
    code: contracts.escrow,
    scope: contracts.escrow,
    table: "rates",
    limit,
  });
}

/** One row of escrowbacker's `escrows` table — one row per currently-live
 * backed asset (removed once it's redeemed/burned), so this table's
 * length is exactly "how many backed NFTs are outstanding right now" and
 * summing `backing.quantity` by symbol is exactly how much value is
 * currently locked. Admin-panel use only. */
export interface EscrowRecord {
  asset_id: string;
  backing: { quantity: string; contract: string };
  minted_at: string;
}

export async function listEscrows(limit = 200): Promise<EscrowRecord[]> {
  return getTableRows<EscrowRecord>({
    rpcUrl: chain.url,
    code: contracts.escrow,
    scope: contracts.escrow,
    table: "escrows",
    limit,
  });
}

export async function getDrop(dropId: number | string): Promise<NftDrop | null> {
  const rows = await getTableRows<NftDrop>({
    rpcUrl: chain.url,
    code: contracts.nftdrops,
    scope: contracts.nftdrops,
    table: "drops",
    lower_bound: String(dropId),
    upper_bound: String(dropId),
    limit: 1,
  });
  return rows[0] ?? null;
}

/** Is `account` whitelisted for this drop, and what's their override mint
 * cap (0 = use the drop's default max_per_wallet)? Only meaningful for
 * whitelist_enabled drops. */
export async function getWhitelistEntry(
  dropId: number | string,
  account: string
): Promise<{ account: string; max_mints: number } | null> {
  const rows = await getTableRows<{ account: string; max_mints: number }>({
    rpcUrl: chain.url,
    code: contracts.nftdrops,
    scope: String(dropId),
    table: "whitelist",
    lower_bound: account,
    upper_bound: account,
    key_type: "name",
    limit: 1,
  });
  return rows[0] ?? null;
}

/** How many times has `account` already minted this drop? */
export async function getWalletMintCount(dropId: number | string, account: string): Promise<number> {
  const rows = await getTableRows<{ account: string; count: number }>({
    rpcUrl: chain.url,
    code: contracts.nftdrops,
    scope: String(dropId),
    table: "walletmints",
    lower_bound: account,
    upper_bound: account,
    key_type: "name",
    limit: 1,
  });
  return rows[0]?.count ?? 0;
}

/**
 * Escrow-backed NFTs (minted through escrowbacker) have NO protocol-level
 * `backed_tokens` on AtomicAssets — their backing lives in escrowbacker's
 * `escrows` table, keyed by asset_id. This looks each asset up there and,
 * when found, fills in `backed_tokens` in the same shape AtomicAssets uses,
 * so every existing backing badge / Redeem button (utils/backing.ts
 * getBackingInfo) works for escrow-backed NFTs without special-casing.
 * Redemption itself is unchanged: burnasset triggers escrowbacker's payout.
 */
export async function attachEscrowBacking<T extends import("../types").AtomicAsset>(assets: T[]): Promise<T[]> {
  const lookups = await Promise.all(
    assets.map(async (asset) => {
      if (asset.backed_tokens && asset.backed_tokens.length > 0) return null;
      try {
        const rows = await getTableRows<EscrowRecord>({
          rpcUrl: chain.url,
          code: contracts.escrow,
          scope: contracts.escrow,
          table: "escrows",
          lower_bound: asset.asset_id,
          upper_bound: asset.asset_id,
          limit: 1,
        });
        const row = rows[0];
        return row && String(row.asset_id) === String(asset.asset_id) ? row : null;
      } catch {
        return null;
      }
    })
  );
  return assets.map((asset, i) => {
    const row = lookups[i];
    if (!row) return asset;
    const [amountStr, symbol] = row.backing.quantity.split(" ");
    const precision = (amountStr.split(".")[1] || "").length;
    const raw = amountStr.replace(".", "").replace(/^0+(?=\d)/, "");
    return { ...asset, backed_tokens: [{ token_symbol: symbol, amount: raw, token_precision: precision }] };
  });
}
